import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  UseOfForceHygieneRestraintLogModel,
  UseOfForceHygieneRestraintLogStatus,
  UseOfForceHygieneRestraintModel,
} from '../../../../api-service/clients';
import { CardComponent } from '../../../../components/card-component';
import { ColorPalette } from '../../../../components/config';
import { EditIcon } from '../../../../components/icons/edit-icon/edit-icon';
import { TrashIcon } from '../../../../components/icons/trash-icon/trash-icon';
import { formatDate } from '../../../../utilities';
import {
  approveUseOfForceHygieneRestraintLog,
  correctUseOfForceHygieneRestraint,
  getUseOfForceHygieneRestraintLogs,
  openUseOfForceHygieneRestraintLogPdf,
  registerUseOfForceHygieneRestraint,
  registerUseOfForceHygieneRestraintLogMunicipalitySending,
  removeUseOfForceHygieneRestraint,
  UseOfForceHygieneRestraintInput,
} from './use-of-force-api';
import { UseOfForceHygieneRestraintDialog } from './use-of-force-hygiene-restraint-dialog';
import { hygieneRestraintLogStatusLabels } from './use-of-force-labels';
import { UseOfForceMunicipalitySendingDialog } from '../../../service-provider-use-of-force/use-of-force-municipality-sending-dialog';

interface UseOfForceHygieneRestraintsCardProps {
  citizenId: string;
  serviceProviderUnitId: string;
  reloadCount: number;
  canRegister: boolean;
  hasSection136cIncident: boolean;
}

type OpenDialog =
  | { kind: 'register' }
  | { kind: 'municipalitySending'; log: UseOfForceHygieneRestraintLogModel }
  | {
      kind: 'correct';
      log: UseOfForceHygieneRestraintLogModel;
      restraint: UseOfForceHygieneRestraintModel;
    };

// SBST's Bilag til § 136 c: the month's repeated holds after its § 136 c Skema 1 (decision 89).
export function UseOfForceHygieneRestraintsCard(
  props: UseOfForceHygieneRestraintsCardProps
) {
  const {
    citizenId,
    serviceProviderUnitId,
    reloadCount,
    canRegister,
    hasSection136cIncident,
  } = props;
  const [logs, setLogs] = useState<UseOfForceHygieneRestraintLogModel[]>([]);
  const [currentUserName, setCurrentUserName] = useState('');
  const [canAdminister, setCanAdminister] = useState(false);
  const [openDialog, setOpenDialog] = useState<OpenDialog>();
  const [ownReloadCount, setOwnReloadCount] = useState(0);

  useEffect(() => {
    let isCurrent = true;
    async function load() {
      const result = await getUseOfForceHygieneRestraintLogs(
        citizenId,
        serviceProviderUnitId
      );
      if (!isCurrent) return;
      setLogs(result.logs);
      setCurrentUserName(result.currentUserName);
      setCanAdminister(result.canAdminister);
    }
    load();
    return () => {
      isCurrent = false;
    };
  }, [citizenId, serviceProviderUnitId, reloadCount, ownReloadCount]);

  if (logs.length === 0 && !hasSection136cIncident) return null;

  async function saveAndReload(action: () => Promise<unknown>) {
    await action();
    setOpenDialog(undefined);
    setOwnReloadCount(count => count + 1);
  }

  return (
    <CardComponent
      title="Bilag til § 136 c"
      contentBackgroundColor={ColorPalette.WHITE}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 1,
        }}
      >
        <Typography variant="body2">
          Gentagne fastholdelser i personlige hygiejnesituationer registreres
          her. Den første i måneden registreres på Skema 1.
        </Typography>
        {canRegister && (
          <Button
            variant="outlined"
            onClick={() => setOpenDialog({ kind: 'register' })}
          >
            Registrér fastholdelse
          </Button>
        )}
      </Box>
      {logs.length === 0 ? (
        <Typography variant="body2">
          Der er ingen fastholdelser i bilaget.
        </Typography>
      ) : (
        logs.map(log => (
          <Box key={log.id} sx={{ mt: 2 }}>
            <Typography variant="subtitle2">
              {monthLabel(log.month)} ·{' '}
              {hygieneRestraintLogStatusLabels[log.status]}
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Dato</TableCell>
                  <TableCell>Tidsrum</TableCell>
                  <TableCell>Medarbejder</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {log.restraints.map(restraint => (
                  <TableRow key={restraint.id}>
                    <TableCell>{formatDate(restraint.startedAt)}</TableCell>
                    <TableCell>{`${formatDate(
                      restraint.startedAt,
                      'HH:mm'
                    )} - ${formatDate(restraint.endedAt, 'HH:mm')}`}</TableCell>
                    <TableCell>{restraint.employeeName}</TableCell>
                    <TableCell align="right">
                      {restraint.isOwn &&
                        canRegister &&
                        log.status ===
                          UseOfForceHygieneRestraintLogStatus.Draft && (
                          <>
                            <Tooltip title="Ret fastholdelsen">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  setOpenDialog({
                                    kind: 'correct',
                                    log,
                                    restraint,
                                  })
                                }
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Slet fastholdelsen">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  saveAndReload(() =>
                                    removeUseOfForceHygieneRestraint(
                                      citizenId,
                                      serviceProviderUnitId,
                                      log.id,
                                      restraint.id
                                    )
                                  )
                                }
                              >
                                <TrashIcon />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <LogActions
              log={log}
              canAdminister={canAdminister}
              onOpenPdf={() =>
                openUseOfForceHygieneRestraintLogPdf(
                  citizenId,
                  serviceProviderUnitId,
                  log.id
                )
              }
              onApprove={() =>
                saveAndReload(() =>
                  approveUseOfForceHygieneRestraintLog(
                    citizenId,
                    serviceProviderUnitId,
                    log.id
                  )
                )
              }
              onRegisterMunicipalitySending={() =>
                setOpenDialog({ kind: 'municipalitySending', log })
              }
            />
          </Box>
        ))
      )}
      {openDialog?.kind === 'municipalitySending' && (
        <UseOfForceMunicipalitySendingDialog
          municipalityName={openDialog.log.responsibleMunicipalityName}
          description={`Borgerens handlekommune er ${
            openDialog.log.responsibleMunicipalityName ?? 'en anden kommune'
          }. Hent bilaget som PDF, send det til handlekommunen, og registrér her, hvornår det blev sendt. Registreringen kan ikke ændres bagefter.`}
          onSave={sentAt =>
            saveAndReload(() =>
              registerUseOfForceHygieneRestraintLogMunicipalitySending(
                citizenId,
                serviceProviderUnitId,
                openDialog.log.id,
                sentAt
              )
            )
          }
          onClose={() => setOpenDialog(undefined)}
        />
      )}
      {openDialog?.kind === 'register' && (
        <UseOfForceHygieneRestraintDialog
          title="Registrér fastholdelse"
          employeeName={currentUserName}
          onSave={(restraint: UseOfForceHygieneRestraintInput) =>
            saveAndReload(() =>
              registerUseOfForceHygieneRestraint(
                citizenId,
                serviceProviderUnitId,
                restraint
              )
            )
          }
          onClose={() => setOpenDialog(undefined)}
        />
      )}
      {openDialog?.kind === 'correct' && (
        <UseOfForceHygieneRestraintDialog
          title="Ret fastholdelse"
          employeeName={openDialog.restraint.employeeName}
          startedAt={new Date(openDialog.restraint.startedAt)}
          endedAt={new Date(openDialog.restraint.endedAt)}
          onSave={(restraint: UseOfForceHygieneRestraintInput) =>
            saveAndReload(() =>
              correctUseOfForceHygieneRestraint(
                citizenId,
                serviceProviderUnitId,
                openDialog.log.id,
                openDialog.restraint.id,
                restraint
              )
            )
          }
          onClose={() => setOpenDialog(undefined)}
        />
      )}
    </CardComponent>
  );
}

interface LogActionsProps {
  log: UseOfForceHygieneRestraintLogModel;
  canAdminister: boolean;
  onOpenPdf: () => void;
  onApprove: () => void;
  onRegisterMunicipalitySending: () => void;
}

// The leader approves the month's bilag, which then goes to the handlekommune as its Skema 1 did (decision 90).
function LogActions(props: LogActionsProps) {
  const {
    log,
    canAdminister,
    onOpenPdf,
    onApprove,
    onRegisterMunicipalitySending,
  } = props;
  const isDraft = log.status === UseOfForceHygieneRestraintLogStatus.Draft;
  const awaitsMunicipalitySending =
    !isDraft &&
    log.responsibleMunicipalityIsOwnMunicipality === false &&
    !log.sentToResponsibleMunicipalityAt;

  return (
    <Box sx={{ mt: 1 }}>
      {!isDraft && (
        <Typography variant="body2">
          Handlekommune: {log.responsibleMunicipalityName ?? '-'} ·{' '}
          {log.sentToResponsibleMunicipalityAt
            ? `sendt ${formatDate(
                log.sentToResponsibleMunicipalityAt,
                'dd-MM-yyyy HH:mm'
              )}`
            : 'ikke sendt'}
        </Typography>
      )}
      {isDraft && canAdminister && log.missingForApprove.length > 0 && (
        <Typography variant="body2" color="text.secondary">
          {log.missingForApprove.join(' ')}
        </Typography>
      )}
      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
        <Button size="small" variant="outlined" onClick={onOpenPdf}>
          Hent PDF
        </Button>
        {isDraft && canAdminister && (
          <Button
            size="small"
            variant="contained"
            onClick={onApprove}
            disabled={log.missingForApprove.length > 0}
          >
            Godkend bilag
          </Button>
        )}
        {awaitsMunicipalitySending && canAdminister && (
          <Button
            size="small"
            variant="contained"
            onClick={onRegisterMunicipalitySending}
          >
            Registrér afsendelse
          </Button>
        )}
      </Box>
    </Box>
  );
}

function monthLabel(month: Date | string): string {
  const label = new Date(month).toLocaleDateString('da-DK', {
    month: 'long',
    year: 'numeric',
  });
  return label.charAt(0).toUpperCase() + label.slice(1);
}
